'use client'

import { cx } from 'class-variance-authority'
import { PlusIcon } from '@phosphor-icons/react/dist/ssr/Plus'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { format } from 'date-fns'
import { Badge, Button, DropdownMenu, Input } from '@skop-app/lib-front'
import { Value } from '@skop-app/lib-domain/types'

import { Select } from '@/components/atoms'
import { matchKey } from '@/utils'

type MapFilterProps = {
  setMapFilters: React.Dispatch<React.SetStateAction<object>>
  mapFilters: {
    category?: string
    status?: string
    startDate?: string
    endDate?: string
  }
  totalLabel: string
  options: {
    label: string
    value: string
  }[]
  statusOptions: {
    label: string
    value: string
  }[]
  storageKey: string
}

export const MapFilter = (props: MapFilterProps) => {
  const {
    setMapFilters,
    mapFilters,
    totalLabel,
    options,
    statusOptions,
    storageKey,
  } = props

  const handleSaveFilter = (
    filterKey: string,
    filterValue: string | Value | undefined,
  ) => {
    const newFilters = { ...mapFilters, [filterKey]: filterValue }
    setMapFilters(newFilters)
    localStorage.setItem(storageKey, JSON.stringify(newFilters))
  }

  return (
    <div className="items-center justify-between sm:flex">
      {/* filters bar */}
      <div className="flex flex-col space-y-2">
        <div className="flex flex-row items-center space-x-2">
          <Select
            key={mapFilters.category || 'empty'}
            name="Options"
            options={options}
            value={options.find(
              (option) => option.value === mapFilters.category,
            )}
            onChange={(option) => handleSaveFilter('category', option?.value)}
            className="w-56"
            placeholder="Toutes les catégories"
            isClearable
          />

          <div className="flex flex-wrap gap-2">
            {!!mapFilters.startDate && (
              <FilterChip
                label={`Début: ${format(
                  new Date(mapFilters.startDate),
                  'dd/MM/yyyy',
                )}`}
                onRemove={() => handleSaveFilter('startDate', undefined)}
              />
            )}
            {!!mapFilters.endDate && (
              <FilterChip
                label={`Fin: ${format(
                  new Date(mapFilters.endDate),
                  'dd/MM/yyyy',
                )}`}
                onRemove={() => handleSaveFilter('endDate', undefined)}
              />
            )}
            {!!mapFilters.status && (
              <FilterChip
                label={`Statut: ${matchKey(mapFilters.status, statusOptions).label}`}
                onRemove={() => handleSaveFilter('status', undefined)}
              />
            )}
          </div>

          <FilterMenu
            handleSaveFilter={handleSaveFilter}
            mapFilters={mapFilters}
            setMapFilters={setMapFilters}
            statusOptions={statusOptions}
            storageKey={storageKey}
          />
        </div>
      </div>
      <p className="text-sm leading-6 text-slate-300">{totalLabel}</p>
    </div>
  )
}

const FilterMenu = ({
  handleSaveFilter,
  mapFilters,
  setMapFilters,
  statusOptions,
  storageKey,
}) => {
  const resetFilters = () => {
    setMapFilters({})
    localStorage.removeItem(storageKey)
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="outline">
          <PlusIcon className="text-xl" /> Filtres
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="min-w-48 md:min-w-56">
        <DropdownMenu.Group>
          <DropdownMenu.Label> Statut </DropdownMenu.Label>
          <DropdownMenu.Separator />
          {statusOptions.map((status) => {
            return (
              <DropdownMenu.Item
                key={status.label}
                className={cx(
                  status.value === mapFilters.status && 'bg-orange-500',
                )}
                onClick={() => handleSaveFilter('status', status.value)}
              >
                {status.label}
              </DropdownMenu.Item>
            )
          })}
        </DropdownMenu.Group>

        <DropdownMenu.Group>
          <DropdownMenu.Label> Date de disponibilité </DropdownMenu.Label>
          <DropdownMenu.Separator />
          <div className="flex flex-col justify-center space-y-4 p-2">
            <div className="flex flex-col">
              <span className="mb-1 text-sm"> À partir de </span>
              <Input
                type="date"
                defaultValue={mapFilters.startDate}
                max={mapFilters.endDate}
                onChange={(e) => handleSaveFilter('startDate', e.target.value)}
              />
            </div>

            <div className="flex flex-col">
              <span className="mb-1 text-sm"> Jusqu&apos;à </span>
              <Input
                type="date"
                defaultValue={mapFilters.endDate}
                onChange={(e) => handleSaveFilter('endDate', e.target.value)}
                min={mapFilters.startDate}
              />
            </div>
          </div>
        </DropdownMenu.Group>
        <DropdownMenu.Group>
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            className="hover:bg-transparent hover:text-orange-500"
            onClick={() => resetFilters()}
          >
            Réinitialiser les filtres
          </DropdownMenu.Item>
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
const FilterChip = ({ label, onRemove }) => (
  <Badge variant="dark" className="flex items-center gap-2 py-2.5 shadow-none">
    {label}
    <div onClick={onRemove} className="flex items-center justify-center">
      <span className="sr-only">Supprimer le filtre</span>
      <XIcon className="size-3 text-white" />
    </div>
  </Badge>
)
